import { z } from "zod";

export const BriefingLinkSchema = z
  .string()
  .url()
  .refine((value) => new URL(value).protocol === "https:", "HTTPS link required");
export const BriefingItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  url: BriefingLinkSchema,
  label: z.string(),
  draft: z.boolean().optional(),
  author: z.string().optional(),
  createdAt: z.string().datetime().optional(),
  kind: z.enum(["issue", "pull_request"]),
  project: z.string().optional(),
  assignee: z.string().optional(),
});
export const BriefingIssueSchema = BriefingItemSchema.extend({
  description: z.string(),
  status: z.string().optional(),
  priority: z.string().optional(),
});
export const BriefingAnalyticsSchema = z.object({
  title: z.string(),
  period: z.string(),
  updatedAt: z.string().nullable(),
  error: z.string().nullable(),
  metrics: z.array(
    z.object({
      label: z.string(),
      value: z.number().nullable(),
      format: z.enum(["number", "currency"]),
      currency: z.string().optional(),
      description: z.string(),
    }),
  ),
});
/** Deployment-owned source mapping; never sent to clients. */
export const BriefingConfigSchema = z.object({
  userId: z.string(),
  spaceId: z.string(),
  botId: z.string(),
  github: z.object({ repository: z.string(), author: z.string(), tokenSecretId: z.string() }),
  linear: z.object({
    serverId: z.string(),
    toolName: z.string(),
    detailToolName: z.string(),
    state: z.string(),
    assignee: z.string(),
    colleague: z.string(),
    project: z.string(),
    projectName: z.string(),
  }),
  analytics: z
    .object({
      serverId: z.string(),
      projectId: z.number().int().positive(),
      region: z.enum(["us", "eu"]),
      query: z.string(),
      title: z.string(),
      period: z.string(),
      metrics: z.array(
        z.object({
          column: z.string(),
          label: z.string(),
          format: z.enum(["number", "currency"]),
          currency: z.string().optional(),
          description: z.string(),
        }),
      ),
    })
    .optional(),
});
export const BriefingSectionSchema = z.object({
  title: z.string(),
  items: z.array(BriefingItemSchema),
  updatedAt: z.string().nullable(),
  error: z.string().nullable(),
});
export const MissionBriefingSchema = z.object({
  sections: z.array(BriefingSectionSchema),
  needsYou: z.array(
    z.object({
      runId: z.string(),
      missionId: z.string().nullable().optional(),
      botId: z.string(),
      botName: z.string(),
      groupId: z.string().nullable(),
      reason: z.string(),
    }),
  ),
  refreshing: z.boolean(),
  configured: z.boolean(),
  analytics: BriefingAnalyticsSchema.nullable(),
});
export type MissionBriefing = z.infer<typeof MissionBriefingSchema>;
export type BriefingSection = z.infer<typeof BriefingSectionSchema>;
export type BriefingIssue = z.infer<typeof BriefingIssueSchema>;

/** Playful presence copy, never a claim that a particular tool or check ran. */
export const WORKING_PHRASES = [
  "On it…",
  "Thinking it through…",
  "Working on it…",
  "Still on the case…",
  "Making progress…",
  "Still working…",
] as const;
